import { useMemo } from 'react';
import { HistoryType } from 'src/@types/historyTables';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import DataApiClient, {
	ShortTermDataApiPathParams,
	ShortTermDataApiQueryParams,
} from '../../utils/DataApiClient';
import useCurrentAuthority from '../useCurrentAuthority';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import { useInfiniteQuery } from '@tanstack/react-query';
import type {
	InfiniteData,
	QueryFunctionContext,
	QueryKey,
} from '@tanstack/react-query';

interface PageData<T> {
	records: T[];
	nextPage: string | null;
}

interface UseShortTermHistoryQueryResult<T> {
	/** All records fetched so far (flattened from all pages) */
	allRecords: T[];
	/** Whether the initial query is loading */
	isLoading: boolean;
	/** Whether any fetch is in progress */
	isFetching: boolean;
	/** Whether at least one fetch has completed */
	isFetched: boolean;
	/** Whether there are more pages to fetch */
	hasNextPage: boolean;
	/** Fetch the next page of data */
	fetchNextPage: () => void;
	/** Total number of pages fetched */
	totalPagesFetched: number;
	/** Refetch the query */
	refetch: () => void;
}

/**
 * Pure data fetching hook for short-term history data.
 * Uses React Query's useInfiniteQuery for pagination.
 * Does NOT manage UI state - only fetches and returns data.
 */
export const useShortTermHistoryQuery = <T,>(
	historyType: HistoryType,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	deserializer: (r: any) => T,
	enabled: boolean,
	params?: ShortTermDataApiPathParams & ShortTermDataApiQueryParams
): UseShortTermHistoryQueryResult<T> => {
	const currentAccountPubKey = useDriftAccountStore((s) =>
		s.getCurrentUserAccount()?.pubKey?.toString()
	);
	const currentAuthority = useCurrentAuthority();
	const stableParams = JSON.stringify(params);
	const userAccount: string = currentAccountPubKey ?? '';
	const authorityStr: string = currentAuthority?.toString() ?? '';

	const queryEnabled = enabled && userAccount !== '' && authorityStr !== '';

	const query = useInfiniteQuery<
		PageData<T>,
		Error,
		InfiniteData<PageData<T>, string | undefined>,
		QueryKey,
		string | undefined
	>({
		queryKey: createQueryKey(
			'short-term-history',
			historyType,
			userAccount,
			authorityStr,
			stableParams
		),
		queryFn: async ({
			pageParam,
		}: QueryFunctionContext<QueryKey, string | undefined>) => {
			const res = await DataApiClient.fetchShortTermData<T>(
				historyType,
				pageParam,
				{
					userAccount,
					authority: authorityStr,
					...(params ?? {}),
				}
			);
			return {
				records: res.records.map((r) =>
					deserializer(r as unknown as Record<string, unknown>)
				),
				nextPage: res.meta.nextPage,
			};
		},
		getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
		initialPageParam: undefined,
		enabled: queryEnabled,
		refetchOnWindowFocus: false,
		staleTime: 30_000,
	});

	const allRecords = useMemo(() => {
		return query.data?.pages?.flatMap((p) => p.records) ?? [];
	}, [query.data?.pages]);

	return {
		allRecords,
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		isFetched: query.isFetched,
		hasNextPage: Boolean(query.hasNextPage),
		fetchNextPage: query.fetchNextPage,
		totalPagesFetched: query.data?.pages?.length ?? 0,
		refetch: query.refetch,
	};
};
